import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { motion } from 'motion/react'
import { PixelIcon } from './PixelIcon'
import type { PixelIconName } from '@/game/pixelIcons'
import { useGameAudio } from '@/contexts/GameAudioProvider'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

interface GameButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'> {
  variant?: Variant
  size?: Size
  icon?: PixelIconName
  iconRight?: PixelIconName
  loading?: boolean
  fullWidth?: boolean
  silent?: boolean
}

const sizeMap: Record<Size, string> = {
  sm: 'text-sm px-4 min-h-[40px] gap-2',
  md: 'text-base px-5 min-h-[48px] gap-2.5',
  lg: 'text-lg px-7 min-h-[56px] gap-3',
}

const variantMap: Record<Variant, string> = {
  primary: 'text-[#231404] bg-[var(--ember)] shadow-raised',
  secondary: 'text-[var(--text)] bg-[var(--surface-raised)] shadow-emboss',
  ghost: 'text-[var(--text-dim)] bg-transparent border border-[var(--edge)]',
  danger: 'text-white bg-[var(--danger)] shadow-raised',
}

export const GameButton = forwardRef<HTMLButtonElement, GameButtonProps>(function GameButton(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconRight,
    loading = false,
    fullWidth = false,
    silent = false,
    disabled,
    className = '',
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const audio = useGameAudio()
  const isDisabled = disabled || loading

  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={isDisabled}
      whileTap={isDisabled ? undefined : { scale: 0.96 }}
      whileHover={isDisabled ? undefined : { y: -1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      onClick={(e) => {
        if (isDisabled) return
        if (!silent) audio.play('click')
        onClick?.(e)
      }}
      className={[
        'relative inline-flex items-center justify-center rounded-[var(--r-md)] font-semibold select-none',
        'font-display tracking-wide transition-colors',
        sizeMap[size],
        variantMap[variant],
        fullWidth ? 'w-full' : '',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'active:brightness-95',
        className,
      ].join(' ')}
      {...rest}
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <PixelIcon name="loading" size={18} className="animate-spin" />
          <span>Loading</span>
        </span>
      ) : (
        <>
          {icon && <PixelIcon name={icon} size={size === 'lg' ? 22 : 18} />}
          {children != null && <span>{children}</span>}
          {iconRight && <PixelIcon name={iconRight} size={size === 'lg' ? 22 : 18} />}
        </>
      )}
    </motion.button>
  )
})
